import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  CreditCard, 
  Crown, 
  ArrowUpRight, 
  ArrowDownRight, 
  Calendar, 
  Check, 
  Receipt,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { PLANS, formatPrice, SubscriptionTier, PLAN_LIMITS } from '@/lib/pricing';
import { PaymentModal } from '@/components/PaymentModal';
import { useToast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface PaymentRecord {
  id: string;
  payment_id: string;
  order_id: string;
  amount: number;
  currency: string;
  status: string;
  created_at: string;
}

export function SubscriptionManagement() {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [paymentHistory, setPaymentHistory] = useState<PaymentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState<SubscriptionTier>('pro');
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('monthly');
  const [downgrading, setDowngrading] = useState(false);

  const currentTier = (profile?.subscription || 'free') as SubscriptionTier;
  const currentPlan = PLANS.find(p => p.tier === currentTier);
  const currentLimits = PLAN_LIMITS[currentTier];

  useEffect(() => {
    if (!user) return;
    
    const fetchPaymentHistory = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('payment_history')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (!error && data) {
        setPaymentHistory(data);
      }
      setLoading(false);
    };

    fetchPaymentHistory();
  }, [user]);

  const handleUpgrade = (tier: SubscriptionTier) => {
    setSelectedTier(tier);
    setPaymentModalOpen(true);
  };

  const handlePaymentSuccess = () => {
    window.location.reload();
  };

  const handleDowngrade = async (tier: SubscriptionTier) => {
    if (!user) return;
    
    setDowngrading(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ subscription: tier })
        .eq('id', user.id);

      if (error) throw error;

      // Log the downgrade
      await supabase.from('logs').insert({
        user_id: user.id,
        msg: `Downgraded to ${tier} plan`,
        level: 'info',
        time: new Date().toLocaleTimeString()
      });

      await supabase.from('notifications').insert({
        user_id: user.id,
        title: 'Plan Downgraded',
        message: `Your subscription has been changed to ${tier}. Changes take effect at the end of your billing period.`,
        type: 'info',
        read: false
      });

      toast({
        title: 'Plan Downgraded',
        description: `Your subscription has been changed to ${tier}.`,
      });

      window.location.reload();
    } catch (error: any) {
      toast({
        title: 'Downgrade Failed',
        description: error.message || 'Failed to downgrade subscription.',
        variant: 'destructive',
      });
    } finally {
      setDowngrading(false);
    }
  };

  const getTierBadgeColor = (tier: SubscriptionTier) => {
    switch (tier) {
      case 'business': return 'bg-gradient-to-r from-primary to-accent text-white';
      case 'pro': return 'bg-primary/10 text-primary border-primary/30';
      case 'starter': return 'bg-accent/10 text-accent border-accent/30';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getUpgradeTiers = (): SubscriptionTier[] => {
    const order: SubscriptionTier[] = ['free', 'starter', 'pro', 'business'];
    const currentIndex = order.indexOf(currentTier);
    return order.slice(currentIndex + 1);
  };

  const getDowngradeTiers = (): SubscriptionTier[] => {
    const order: SubscriptionTier[] = ['free', 'starter', 'pro', 'business'];
    const currentIndex = order.indexOf(currentTier);
    return order.slice(0, currentIndex);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <>
      {/* Current Plan Card */}
      <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-transparent">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-3">
              <Crown className="text-primary" size={24} />
              Subscription & Billing
            </CardTitle>
            <Badge className={getTierBadgeColor(currentTier)}>
              {currentTier.toUpperCase()}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Current Plan Details */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Current Plan</p>
              <p className="text-2xl font-bold">{currentPlan?.name || 'Free'}</p>
              <p className="text-sm text-muted-foreground">{currentPlan?.description}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Monthly Price</p>
              <p className="text-2xl font-bold">{formatPrice(currentPlan?.price || 0)}</p>
              {currentTier !== 'free' && (
                <p className="text-sm text-muted-foreground">
                  or {formatPrice(currentPlan?.annualPrice || 0)}/year (save 20%)
                </p>
              )}
            </div>
          </div>

          <Separator />

          {/* Plan Limits */}
          <div>
            <h4 className="text-sm font-medium mb-3">Your Plan Limits</h4>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="p-3 rounded-lg bg-surface border border-border/50">
                <p className="text-xs text-muted-foreground">Transactions/month</p>
                <p className="font-semibold">
                  {currentLimits.transactions === -1 ? 'Unlimited' : currentLimits.transactions.toLocaleString()}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-surface border border-border/50">
                <p className="text-xs text-muted-foreground">Invoice Scans</p>
                <p className="font-semibold">
                  {currentLimits.invoiceScans === -1 ? 'Unlimited' : currentLimits.invoiceScans.toLocaleString()}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-surface border border-border/50">
                <p className="text-xs text-muted-foreground">Team Members</p>
                <p className="font-semibold">{currentLimits.teamMembers}</p>
              </div>
              <div className="p-3 rounded-lg bg-surface border border-border/50">
                <p className="text-xs text-muted-foreground">API Calls/day</p>
                <p className="font-semibold">
                  {currentLimits.apiCallsPerDay === -1 ? 'Unlimited' : currentLimits.apiCallsPerDay.toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Plan Actions */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium">Manage Subscription</h4>
            
            <div className="flex flex-wrap gap-3">
              {/* Upgrade Options */}
              {getUpgradeTiers().map((tier) => {
                const plan = PLANS.find(p => p.tier === tier);
                if (!plan) return null;
                return (
                  <Button 
                    key={tier} 
                    onClick={() => handleUpgrade(tier)}
                    className="gap-2"
                  >
                    <ArrowUpRight size={16} />
                    Upgrade to {plan.name}
                  </Button>
                );
              })}

              {/* Downgrade Options */}
              {getDowngradeTiers().length > 0 && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" className="gap-2">
                      <ArrowDownRight size={16} />
                      Downgrade Plan
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Downgrade Subscription</AlertDialogTitle>
                      <AlertDialogDescription>
                        Downgrading will reduce your plan limits. Any data exceeding the new limits will be preserved but may become inaccessible until you upgrade again.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="space-y-2 py-4">
                      {getDowngradeTiers().map((tier) => {
                        const plan = PLANS.find(p => p.tier === tier);
                        if (!plan) return null;
                        return (
                          <Button
                            key={tier}
                            variant="outline"
                            className="w-full justify-between"
                            onClick={() => handleDowngrade(tier)}
                            disabled={downgrading}
                          >
                            <span>{plan.name}</span>
                            <span className="text-muted-foreground">
                              {plan.price === 0 ? 'Free' : formatPrice(plan.price) + '/mo'}
                            </span>
                          </Button>
                        );
                      })}
                    </div>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}

              {/* View All Plans */}
              <Button variant="ghost" onClick={() => navigate('/pricing')}>
                Compare All Plans
              </Button>
            </div>

            {currentTier !== 'free' && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
                <AlertCircle size={16} />
                <span>
                  Plan changes take effect at the end of your current billing period. 
                  You'll retain access to your current features until then.
                </span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Payment History Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            <Receipt className="text-primary" size={24} />
            Payment History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : paymentHistory.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No payment history yet</p>
              <p className="text-sm mt-1">
                Payments will appear here after you upgrade your subscription.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {paymentHistory.map((payment) => (
                <div 
                  key={payment.id} 
                  className="flex items-center justify-between p-3 rounded-lg border border-border/50 hover:bg-surface/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      payment.status === 'success' ? 'bg-success/10' : 'bg-destructive/10'
                    }`}>
                      {payment.status === 'success' ? (
                        <Check className="w-5 h-5 text-success" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-destructive" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium">
                        {formatPrice(payment.amount)} {payment.currency}
                      </p>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <Calendar size={12} />
                        {formatDate(payment.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge variant={payment.status === 'success' ? 'default' : 'destructive'}>
                      {payment.status}
                    </Badge>
                    <p className="text-xs text-muted-foreground mt-1">
                      {payment.payment_id.slice(0, 12)}...
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Plan Features */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            <Check className="text-primary" size={24} />
            Your Plan Features
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-2 sm:grid-cols-2">
            {currentPlan?.features.map((feature, idx) => (
              <div key={idx} className="flex items-center gap-2 text-sm">
                <Check className="w-4 h-4 text-primary flex-shrink-0" />
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Payment Modal */}
      <PaymentModal
        open={paymentModalOpen}
        onOpenChange={setPaymentModalOpen}
        selectedTier={selectedTier}
        billingPeriod={billingPeriod}
        onSuccess={handlePaymentSuccess}
      />
    </>
  );
}
